"use client";

import React from "react";
import IntroHeader from "../intro/components/IntroHeader";
import IntroLogos from "../intro/components/IntroLogos";
import AlignmentCalEmbed from "./components/AlignmentCalEmbed";
import AlignmentContent from "./components/AlignmentContent";

type GsapCore = typeof import("gsap").gsap;

interface AlignmentPageClientProps {
	clientName?: string;
}

export default function AlignmentPageClient({
	clientName,
}: AlignmentPageClientProps) {
	const headerRef = React.useRef<HTMLDivElement>(null);
	const contentRef = React.useRef<HTMLDivElement>(null);
	const logosRef = React.useRef<HTMLDivElement>(null);
	const gsapModuleRef = React.useRef<GsapCore | null>(null);
	const timelineRef = React.useRef<ReturnType<GsapCore["timeline"]> | null>(
		null,
	);

	React.useEffect(() => {
		if (typeof document === "undefined") return;

		const root = document.documentElement;
		const previousOrientation = root.dataset.lenisOrientation;
		root.dataset.lenisOrientation = "vertical";

		return () => {
			if (previousOrientation) {
				root.dataset.lenisOrientation = previousOrientation;
			} else {
				delete root.dataset.lenisOrientation;
			}
		};
	}, []);

	React.useEffect(() => {
		if (typeof window === "undefined") return;

		let cancelled = false;

		const animate = async () => {
			if (!gsapModuleRef.current) {
				const mod = await import("gsap");
				const instance =
					(mod as unknown as { gsap?: GsapCore }).gsap ??
					(mod as unknown as { default?: GsapCore }).default ??
					(mod as unknown as GsapCore);
				gsapModuleRef.current = instance;
			}

			if (cancelled) return;

			const gsap = gsapModuleRef.current!;
			const prefersReducedMotion = window.matchMedia(
				"(prefers-reduced-motion: reduce)",
			).matches;

			if (prefersReducedMotion) return;

			const sections = [
				headerRef.current,
				contentRef.current,
				logosRef.current,
			].filter((node): node is HTMLDivElement => Boolean(node));

			if (!sections.length) return;

			timelineRef.current?.kill();

			gsap.set(sections, {
				opacity: 0,
				y: 24,
				filter: "blur(24px)",
			});

			timelineRef.current = gsap.timeline({
				defaults: { duration: 0.6, ease: "power2.out" },
			});

			timelineRef.current.to(sections, {
				opacity: 1,
				y: 0,
				filter: "blur(0px)",
				stagger: 0.12,
			});
			timelineRef.current.eventCallback("onComplete", () => {
				gsap.set(sections, { clearProps: "transform,filter" });
			});
		};

		void animate();

		return () => {
			cancelled = true;
			timelineRef.current?.kill();
		};
	}, []);

	return (
		<div className="relative flex min-h-screen flex-col bg-white">
			<div className="pointer-events-none fixed inset-x-0 top-0 z-40 p-4">
				<div ref={headerRef}>
					<IntroHeader />
				</div>
			</div>
			<div className="relative z-10 mx-auto flex w-full max-w-[1512px] flex-1 flex-col items-center gap-16 px-4 pb-20 pt-[104px] lg:gap-16 lg:pt-32">
				<div className="flex w-full flex-col items-center gap-8">
					<div
						ref={contentRef}
						className="flex w-full flex-col items-center gap-6"
					>
						<div className="mx-auto flex w-full max-w-[448px] flex-col gap-3 lg:max-w-[948px] lg:flex-row lg:items-stretch lg:gap-6 lg:justify-center">
							<AlignmentContent clientName={clientName} />

							<div className="box-border w-full overflow-hidden rounded-[24px] bg-neutral-900 p-4 lg:h-[672px] lg:w-[448px] lg:overflow-visible">
								<AlignmentCalEmbed className="h-full w-full" />
							</div>
						</div>
					</div>
				</div>

				<div ref={logosRef} className="w-full">
					<IntroLogos />
				</div>
			</div>
		</div>
	);
}
